import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, XCircle, RefreshCw, Rocket, Loader2,
  ShieldCheck, CircleAlert, Clock, ArrowLeft, Eye, EyeOff, Calendar, X,
  Shield, ScanSearch, Send,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Founder-only page — the header below is read server-side to gate the API.
const FOUNDER_EMAIL = 'demo@cartieai.com';

// Status → visual config
const STATUS_STYLES = {
  green: {
    icon: CheckCircle2,
    label: 'OK',
    ring: 'border-emerald-400/40 bg-emerald-500/[0.06]',
    dot: 'bg-emerald-400',
    text: 'text-emerald-200',
    chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  },
  yellow: {
    icon: AlertTriangle,
    label: 'WARN',
    ring: 'border-amber-400/40 bg-amber-500/[0.07]',
    dot: 'bg-amber-400',
    text: 'text-amber-200',
    chip: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  },
  red: {
    icon: XCircle,
    label: 'BLOCK',
    ring: 'border-rose-400/50 bg-rose-500/[0.08]',
    dot: 'bg-rose-400',
    text: 'text-rose-200',
    chip: 'bg-rose-500/15 text-rose-300 border-rose-400/40',
  },
};

const OVERALL_STYLES = {
  ready: {
    label: 'Ready for production',
    description: 'All systems green. Safe to hit the deploy button.',
    gradient: 'from-emerald-500/20 via-emerald-500/10 to-slate-950',
    border: 'border-emerald-400/40',
    ring: 'shadow-emerald-500/20',
    dot: 'bg-emerald-400',
    text: 'text-emerald-200',
  },
  warnings: {
    label: 'Ready with warnings',
    description: 'No blockers, but read the yellow rows before launch.',
    gradient: 'from-amber-500/20 via-amber-500/10 to-slate-950',
    border: 'border-amber-400/40',
    ring: 'shadow-amber-500/20',
    dot: 'bg-amber-400',
    text: 'text-amber-200',
  },
  blocked: {
    label: 'Blocked — fix reds first',
    description: 'At least one check failed. Resolve before deploying.',
    gradient: 'from-rose-500/20 via-rose-500/10 to-slate-950',
    border: 'border-rose-400/50',
    ring: 'shadow-rose-500/20',
    dot: 'bg-rose-400',
    text: 'text-rose-200',
  },
};

const SlackTestPingButton = () => {
  const [pinging, setPinging] = useState(false);
  const send = async () => {
    setPinging(true);
    try {
      const r = await axios.post(
        `${API}/admin/pre-flight/slack-test-ping`,
        {},
        { headers: { 'X-Founder-Email': FOUNDER_EMAIL } }
      );
      if (r.data?.sent) {
        toast.success('Test ping delivered to your Slack channel');
      } else if (r.data?.demo_mode) {
        toast.warning('Slack webhook unset — running in demo mode');
      } else {
        toast.error(r.data?.message || 'Slack returned a non-success status');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send test ping');
    } finally {
      setPinging(false);
    }
  };
  return (
    <button
      type="button"
      onClick={send}
      disabled={pinging}
      data-testid="preflight-slack-test-ping-btn"
      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-cyan-200 transition-colors disabled:opacity-50"
    >
      {pinging ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Send className="w-3 h-3" />
      )}
      Send test ping
    </button>
  );
};

const CheckRow = ({ check }) => {
  const s = STATUS_STYLES[check.status] || STATUS_STYLES.red;
  const Icon = s.icon;
  return (
    <div
      className={`rounded-xl border ${s.ring} px-5 py-4 flex items-start gap-4`}
      data-testid={`preflight-check-${check.id}`}
    >
      <div className="shrink-0 mt-0.5">
        <Icon className={`w-5 h-5 ${s.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <span className="font-semibold text-slate-900 text-sm">
            {check.label}
          </span>
          <Badge
            className={`border font-mono text-[11px] uppercase tracking-wider ${s.chip}`}
          >
            {s.label}
          </Badge>
        </div>
        <p className="text-[12px] text-slate-600/90 leading-relaxed">
          {check.detail}
        </p>
        {check.action && (
          <p className="mt-2 text-[11px] text-slate-400 flex items-start gap-1.5">
            <span className="text-slate-400 font-bold">→</span>
            <span className="italic">{check.action}</span>
          </p>
        )}
        {check.id === 'slack_webhook' && check.status === 'green' && (
          <SlackTestPingButton />
        )}
      </div>
    </div>
  );
};

const StatCell = ({ count, label, statusKey }) => {
  const s = STATUS_STYLES[statusKey];
  const dim = count === 0;
  return (
    <div
      className={`flex-1 px-4 py-3 rounded-lg border ${
        dim ? 'border-slate-800 bg-slate-900 text-slate-100/30' : s.ring
      }`}
      data-testid={`preflight-count-${statusKey}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full ${dim ? 'bg-slate-600' : s.dot}`} />
        <span
          className={`text-[11px] uppercase tracking-[0.18em] font-bold ${
            dim ? 'text-slate-400' : s.text
          }`}
        >
          {label}
        </span>
      </div>
      <div
        className={`text-2xl font-bold tabular-nums ${
          dim ? 'text-slate-400' : 'text-white'
        }`}
      >
        {count}
      </div>
    </div>
  );
};

const AdminPreFlightPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [visibility, setVisibility] = useState(null);
  const [visLoading, setVisLoading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditSaving, setAuditSaving] = useState(false);

  const loadAudit = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/public-audit`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAudit(r.data);
    } catch (e) {
      // Non-fatal — audit card just hides itself
    }
  }, []);

  const markAuditDone = async () => {
    setAuditSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/public-audit/mark-done`,
        { notes: null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setAudit(r.data);
      toast.success('Public audit recorded — next due in 90 days');
    } catch (e) {
      toast.error('Could not record audit');
    } finally {
      setAuditSaving(false);
    }
  };

  const loadVisibility = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/build/visibility`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setVisibility(r.data);
      // Prefill schedule input with current value (if any) in YYYY-MM-DD format
      if (r.data.auto_hide_at) {
        setScheduleDate(r.data.auto_hide_at.slice(0, 10));
      }
    } catch (e) {
      // Non-fatal; visibility section just hides itself
    }
  }, []);

  const toggleVisibility = async () => {
    if (!visibility) return;
    setVisLoading(true);
    try {
      const next = !visibility.enabled;
      const r = await axios.put(
        `${API}/admin/build/visibility`,
        { enabled: next },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setVisibility(r.data);
      toast.success(
        next
          ? 'Build-in-Public pages are now LIVE worldwide'
          : 'Build-in-Public pages are now HIDDEN (returning 404)'
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update visibility');
    } finally {
      setVisLoading(false);
    }
  };

  const saveSchedule = async () => {
    if (!scheduleDate) {
      toast.error('Pick a date first');
      return;
    }
    setScheduleSaving(true);
    try {
      // Convert YYYY-MM-DD to ISO at 00:00 UTC
      const iso = new Date(`${scheduleDate}T00:00:00Z`).toISOString();
      const r = await axios.put(
        `${API}/admin/build/visibility/schedule`,
        { auto_hide_at: iso },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setVisibility(r.data);
      toast.success(`Auto-hide scheduled for ${new Date(iso).toLocaleDateString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  const cancelSchedule = async () => {
    setScheduleSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/build/visibility/schedule`,
        { auto_hide_at: null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setVisibility(r.data);
      setScheduleDate('');
      toast.success('Auto-hide schedule cancelled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    } finally {
      setScheduleSaving(false);
    }
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/pre-flight/checks`, {
        headers: { 'X-Founder-Email': FOUNDER_EMAIL },
      });
      setReport(r.data);
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? 'Founder access required. Log in as the founder email.'
          : `Could not load checklist: ${e?.message || 'unknown error'}`
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadVisibility();
    loadAudit();
  }, [load, loadVisibility, loadAudit]);

  const onRefresh = () => {
    setRefreshing(true);
    load();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          Running pre-flight checks…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center px-6">
        <Card className="bg-rose-500/[0.06] border-rose-400/40 max-w-md">
          <CardContent className="p-6 text-center">
            <CircleAlert className="w-10 h-10 text-rose-300 mx-auto mb-3" />
            <div className="font-bold text-slate-900 mb-2">Access Denied</div>
            <p className="text-[12px] text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overall = report?.overall || 'blocked';
  const overallStyle = OVERALL_STYLES[overall] || OVERALL_STYLES.blocked;

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      data-testid="preflight-page"
    >
      {/* Header */}
      <div className="border-b border-slate-800/70">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Link
            to="/admin/server-modules"
            className="inline-flex items-center gap-2 text-[11px] text-slate-400 hover:text-cyan-300 transition-colors mb-5"
            data-testid="preflight-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back to admin
          </Link>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-cyan-300" />
                <span className="text-[11px] font-bold tracking-[0.22em] text-cyan-300 uppercase">
                  Founder-only · Pre-Flight
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2"><BrandTitle>Deployment Checklist</BrandTitle></h1>
              <p className="text-sm text-slate-400 max-w-xl">
                The last click before you hit deploy. Verifies env vars,
                Stripe mode, email delivery, demo seed, snapshot health,
                monolith state, and the public API.
              </p>
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="preflight-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700 hover:border-slate-500 bg-slate-900 hover:bg-slate-800 text-[12px] font-semibold text-slate-200 transition-all disabled:opacity-50"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
              />
              Re-run
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Overall status hero */}
        <Card
          className={`bg-gradient-to-br ${overallStyle.gradient} border ${overallStyle.border} shadow-xl ${overallStyle.ring} overflow-hidden`}
          data-testid={`preflight-overall-${overall}`}
        >
          <CardContent className="p-7 flex items-center gap-6">
            <div
              className={`shrink-0 w-16 h-16 rounded-2xl bg-slate-950 text-slate-100 border ${overallStyle.border} flex items-center justify-center`}
            >
              {overall === 'ready' ? (
                <Rocket className={`w-7 h-7 ${overallStyle.text}`} />
              ) : overall === 'warnings' ? (
                <AlertTriangle className={`w-7 h-7 ${overallStyle.text}`} />
              ) : (
                <XCircle className={`w-7 h-7 ${overallStyle.text}`} />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`w-2 h-2 rounded-full ${overallStyle.dot} animate-pulse`}
                />
                <span
                  className={`text-[11px] uppercase tracking-[0.22em] font-bold ${overallStyle.text}`}
                >
                  Overall status
                </span>
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1.5">
                {overallStyle.label}
              </h2>
              <p className="text-[13px] text-slate-400">
                {overallStyle.description}
              </p>
              {report?.generated_at && (
                <p className="mt-3 text-[11px] text-slate-400 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  Run at {new Date(report.generated_at).toLocaleString()}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Counts strip */}
        <div className="grid grid-cols-3 gap-3">
          <StatCell
            count={report?.counts?.green || 0}
            label="Green"
            statusKey="green"
          />
          <StatCell
            count={report?.counts?.yellow || 0}
            label="Warnings"
            statusKey="yellow"
          />
          <StatCell
            count={report?.counts?.red || 0}
            label="Blockers"
            statusKey="red"
          />
        </div>

        {/* Check rows */}
        <div className="space-y-3" data-testid="preflight-checks-list">
          {(report?.checks || []).map((c) => (
            <CheckRow key={c.id} check={c} />
          ))}
        </div>

        {/* Build-in-Public visibility toggle */}
        {visibility && (
          <Card
            className={`border ${
              visibility.enabled
                ? 'bg-slate-950 bg-gradient-to-br from-emerald-500/[0.05] to-slate-950 border-emerald-400/30'
                : 'bg-gradient-to-br from-slate-800/40 to-slate-950 border-slate-700'
            }`}
            data-testid="preflight-visibility-toggle-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-5">
                <div
                  className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                    visibility.enabled
                      ? 'bg-emerald-500/15 border border-emerald-400/30 text-emerald-300'
                      : 'bg-slate-800 border border-slate-700 text-slate-400'
                  }`}
                >
                  {visibility.enabled ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[11px] font-bold tracking-[0.22em] text-slate-400 uppercase">
                      Build in Public · Visibility
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold tracking-wide uppercase ${
                        visibility.enabled
                          ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30'
                          : 'bg-slate-700/50 text-slate-300 border border-slate-600'
                      }`}
                      data-testid="preflight-visibility-status"
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          visibility.enabled ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'
                        }`}
                      />
                      {visibility.enabled ? 'LIVE' : 'HIDDEN'}
                    </span>
                  </div>
                  <div className="font-bold text-white text-base mb-1">
                    {visibility.enabled
                      ? '/build/*, /og/milestone/* are publicly accessible'
                      : '/build/*, /og/milestone/* return 404 to the world'}
                  </div>
                  <p className="text-[12px] text-slate-400 mb-3 leading-relaxed">
                    {visibility.enabled
                      ? 'Pre-launch default — keeps the transparency surface open for recruiters, investors, and HN visitors. Flip it off anytime competitors or security concerns change.'
                      : 'The entire public transparency surface is hidden. Visitors, crawlers, and competitors see 404s. Flip back on for recruiting / investor pitches.'}
                  </p>
                  {visibility.updated_at && (
                    <p className="text-[11px] text-slate-400 mb-4">
                      Last changed{' '}
                      {new Date(visibility.updated_at).toLocaleString()}
                      {visibility.updated_by ? ` · by ${visibility.updated_by}` : ''}
                    </p>
                  )}
                  <button
                    onClick={toggleVisibility}
                    disabled={visLoading}
                    data-testid="preflight-visibility-toggle-btn"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-bold tracking-wide uppercase transition-all disabled:opacity-50 ${
                      visibility.enabled
                        ? 'bg-slate-800 border border-slate-600 text-slate-300 hover:bg-slate-700'
                        : 'bg-emerald-500 hover:bg-emerald-400 text-slate-950'
                    }`}
                  >
                    {visLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : visibility.enabled ? (
                      <EyeOff className="w-3.5 h-3.5" />
                    ) : (
                      <Eye className="w-3.5 h-3.5" />
                    )}
                    {visibility.enabled ? 'Hide public pages' : 'Go live worldwide'}
                  </button>

                  {/* Auto-hide schedule — only relevant while enabled */}
                  {visibility.enabled && (
                    <div
                      className="mt-5 pt-5 border-t border-white/10"
                      data-testid="preflight-visibility-schedule"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-[11px] font-bold tracking-[0.22em] text-slate-400 uppercase">
                          Scheduled auto-hide
                        </span>
                      </div>

                      {visibility.auto_hide_at ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="flex-1 min-w-0 text-[13px] text-slate-400">
                            Pages will auto-hide on{' '}
                            <span className="font-bold text-white" data-testid="preflight-visibility-schedule-date">
                              {new Date(visibility.auto_hide_at).toLocaleDateString(
                                undefined,
                                { year: 'numeric', month: 'long', day: 'numeric' }
                              )}
                            </span>
                            <span className="block text-[11px] text-slate-400 mt-0.5">
                              The watchdog runs hourly — expect flip within 1h of target date.
                            </span>
                          </div>
                          <button
                            onClick={cancelSchedule}
                            disabled={scheduleSaving}
                            data-testid="preflight-visibility-schedule-cancel-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold text-slate-400 bg-slate-800 hover:bg-slate-700 border border-slate-600 transition-colors disabled:opacity-50 shrink-0"
                          >
                            {scheduleSaving ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            Cancel schedule
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                          <div className="flex-1 min-w-0">
                            <label
                              htmlFor="preflight-visibility-schedule-input"
                              className="block text-[11px] text-slate-400 mb-1.5"
                            >
                              Pick a date (UTC). We'll auto-flip visibility to HIDDEN on that day.
                            </label>
                            <input
                              id="preflight-visibility-schedule-input"
                              type="date"
                              value={scheduleDate}
                              onChange={(e) => setScheduleDate(e.target.value)}
                              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                              data-testid="preflight-visibility-schedule-input"
                              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/50"
                            />
                          </div>
                          <button
                            onClick={saveSchedule}
                            disabled={scheduleSaving || !scheduleDate}
                            data-testid="preflight-visibility-schedule-save-btn"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-bold uppercase tracking-wide bg-amber-500 hover:bg-amber-400 text-slate-950 transition-colors disabled:opacity-50 shrink-0"
                          >
                            {scheduleSaving ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Calendar className="w-3.5 h-3.5" />
                            )}
                            Schedule
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Public-Surface Audit reminder (Iter84g) */}
        {audit && (
          <Card
            data-testid="preflight-public-audit-card"
            className={`border ${
              audit.never_audited
                ? 'bg-amber-500/[0.05] border-amber-400/30'
                : audit.overdue
                  ? 'bg-rose-500/[0.05] border-rose-400/40'
                  : 'bg-slate-900 text-slate-100 border-slate-800'
            }`}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${
                  audit.never_audited || audit.overdue
                    ? 'bg-rose-500/10 border-rose-400/30 text-rose-300'
                    : 'bg-emerald-500/10 border-emerald-400/30 text-cyan-300'
                }`}>
                  <ScanSearch className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <h3 className="text-base font-semibold text-white">
                      Public-Surface Audit
                    </h3>
                    {audit.never_audited && (
                      <Badge className="bg-amber-500/15 text-amber-300 border-amber-400/30 text-[11px] tracking-widest">
                        NEVER AUDITED
                      </Badge>
                    )}
                    {!audit.never_audited && audit.overdue && (
                      <Badge className="bg-rose-500/15 text-rose-300 border-rose-400/30 text-[11px] tracking-widest">
                        OVERDUE
                      </Badge>
                    )}
                    {!audit.never_audited && !audit.overdue && (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-400/30 text-[11px] tracking-widest">
                        UP TO DATE
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mb-3 leading-relaxed">
                    Manually re-audit which endpoints / pages are reachable
                    without auth, and confirm none of them leak vendor names,
                    feature names, or user data. Quarterly cadence.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mb-4">
                    <div data-testid="audit-last-tile">
                      <div className="text-slate-400 uppercase tracking-widest text-[11px] font-bold mb-1">Last audit</div>
                      <div className="text-slate-300 font-medium">
                        {audit.last_audited_at
                          ? new Date(audit.last_audited_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </div>
                      {audit.last_audited_by && (
                        <div className="text-slate-400 text-[11px] mt-0.5 truncate">{audit.last_audited_by}</div>
                      )}
                    </div>
                    <div data-testid="audit-next-tile">
                      <div className="text-slate-400 uppercase tracking-widest text-[11px] font-bold mb-1">Next due</div>
                      <div className="text-slate-300 font-medium">
                        {audit.next_audit_at
                          ? new Date(audit.next_audit_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </div>
                      {audit.days_until_due != null && (
                        <div className={`text-[11px] mt-0.5 ${audit.overdue ? 'text-rose-400' : 'text-slate-400'}`}>
                          {audit.overdue
                            ? `${Math.abs(Math.round(audit.days_until_due))} days overdue`
                            : `in ${Math.round(audit.days_until_due)} days`}
                        </div>
                      )}
                    </div>
                    <div data-testid="audit-cadence-tile">
                      <div className="text-slate-400 uppercase tracking-widest text-[11px] font-bold mb-1">Cadence</div>
                      <div className="text-slate-300 font-medium">{audit.interval_days} days</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid="audit-mark-done-btn"
                      onClick={markAuditDone}
                      disabled={auditSaving}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-200 text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {auditSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                      I just audited the public surface
                    </button>
                    <Link
                      to="/admin/router-map"
                      data-testid="audit-go-router-map-link"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-slate-400 hover:text-cyan-300 text-xs font-medium transition-colors"
                    >
                      Go to Router Map →
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deploy readiness footer */}
        {overall === 'ready' && (
          <Card
            className="surface-hero surface-hero-accent-strip relative overflow-hidden"
            data-testid="preflight-deploy-footer"
          >
            <CardContent className="p-6 flex items-center gap-5">
              <Rocket className="w-10 h-10 shrink-0" style={{color:'rgb(5, 150, 105)'}} />
              <div className="flex-1">
                <div className="font-extrabold text-slate-900 text-xl mb-1">
                  You're clear to deploy.
                </div>
                <p className="text-[12px] text-slate-500">
                  Hit the deploy button in your hosting platform. Post-deploy,
                  re-run this checklist against the production URL to verify.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPreFlightPage;
